import { CClient, flattenErrorsDictValues } from '@common/utils'
import Button from '@components/atoms/button'
import RegistrationNavBar from '@components/auth/navbar'
import PasswordInput from '@components/auth/password-input'
import TestimonialCard from '@components/auth/testimonial-card'
import DottedCardBackgroundWrapper from '@components/common/dotted-card-bg'
import Spinner from '@components/common/spinner'
import { BROWSER_STORAGE_KEYS } from '@lib/data/browser-storage-keys'
import { getAPIErrorString } from '@lib/utils/common'
import { setToLocalStorage } from '@lib/utils/local-storage'
import { Trans } from '@lingui/macro'
import { captureException, captureMessage } from '@sentry/node'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { AlertTriangle, ArrowLeft } from 'react-feather'
import swal from 'sweetalert'

const TestimonialCardWithDottedBackground = DottedCardBackgroundWrapper(
  TestimonialCard
)

const ForgotPassword = () => {
  const [otp, setOtp] = useState('')
  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')

  const router = useRouter()
  const phoneFromQuery = (router.query?.phone as string) || ''

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const [loadingResendOTP, setLoadingResendOTP] = useState(false)

  const handlePasswordResetWithOTP = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault()
    setLoading(true)
    setErrors([])

    try {
      // TODO: Add password validtion

      // API
      const apiEndpoint = '/api/v1/auth/forgot-password-reset/'
      const apiBody = {
        phone: `+${phoneFromQuery}`,
        otp: otp,
        password: password,
        password2: password2,
      }
      const response = await CClient(apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(apiBody),
      })

      if (!response.ok) {
        const data = await response.json()
        const errors = flattenErrorsDictValues(data)
        setErrors(errors)
        throw new Error(await getAPIErrorString(response, apiEndpoint, apiBody))
      }

      setToLocalStorage(BROWSER_STORAGE_KEYS.HAS_PASSWORD, 'true', () => {
        captureMessage(
          `Failed to set "${BROWSER_STORAGE_KEYS.HAS_PASSWORD}" to true in local storage`
        )
      })

      swal({
        icon: 'success',
        title: 'Success',
        text: 'Password set successfully',
      }).then((hasClosed) => {
        if (hasClosed) {
          router.push(`/login/password/?phone=${phoneFromQuery}`)
        }
      })
    } catch (e) {
      captureException(e)
    } finally {
      setLoading(false)
    }
  }

  const handleOtpResend = async () => {
    setLoadingResendOTP(true)
    setErrors([])

    try {
      const apiEndpoint = '/api/v1/auth/forgot-password-token/'
      const apiBody = {
        phone: `+${[phoneFromQuery]}`,
      }
      const response = await CClient(apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(apiBody),
      })

      if (!response.ok) {
        const data = await response.json()
        const errors = flattenErrorsDictValues(data)
        setErrors(errors)
        throw new Error(await getAPIErrorString(response, apiEndpoint, apiBody))
      }
    } catch (e) {
      captureException(e)
    } finally {
      setLoadingResendOTP(false)
    }
  }

  return (
    <div className="flex flex-col slant-orange-split text-grey">
      <RegistrationNavBar />
      <div className="container mx-auto my-auto flex items-center">
        <div className="hidden w-full pr-5 lg:block sm:w-1/2">
          <TestimonialCardWithDottedBackground />
        </div>
        <div className="max-w-md w-full leading-tight">
          <div className="mb-8">
            <h1 className="text-4xl font-700 mb-1">Reset password</h1>
            {loadingResendOTP && <Spinner classes="h-5 w-5 text-orange" />}
            {!loadingResendOTP && (
              <p>
                <Trans>
                  OTP has been sent to your phone number and associated email
                  address.
                </Trans>{' '}
                <button
                  className="text-orange underline"
                  onClick={handleOtpResend}
                >
                  <Trans>Send again</Trans>
                </button>
              </p>
            )}
          </div>
          <form
            onSubmit={handlePasswordResetWithOTP}
            className="max-w-sm flex flex-col space-y-5 text-grey"
          >
            <fieldset className="flex flex-col space-y-2">
              <label className="text-lg font-600">
                <Trans>Enter OTP</Trans>
              </label>
              <input
                id="otp"
                name="otp"
                placeholder="Enter password reset OTP"
                value={otp}
                onChange={(e) => setOtp(e.currentTarget.value)}
                className="border border-grey-500 rounded py-2 px-4 focus:border-orange focus:outline-none placeholder-grey-800 w-full"
                required
              />
            </fieldset>

            <fieldset className="flex flex-col space-y-2">
              <label className="text-lg font-600">
                <Trans>Enter new password</Trans>
              </label>
              <PasswordInput
                id="password_1"
                name="password_1"
                placeholder="Enter new password"
                required
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
              />
            </fieldset>

            <fieldset className="flex flex-col space-y-2">
              <label className="text-lg font-600">
                <Trans>Confirm password</Trans>
              </label>
              <PasswordInput
                id="password_2"
                name="password_2"
                placeholder="Re-enter your new password"
                value={password2}
                required
                onChange={(e) => setPassword2(e.currentTarget.value)}
              />
            </fieldset>
            {errors.length > 0 &&
              errors.map((error, i) => (
                <div
                  className="flex items-center space-x-2 bg-red-50 border-l-4 border-orange py-1 mt-2"
                  key={i}
                >
                  <AlertTriangle size={15} className="text-red-200 ml-2" />
                  <p className="text-sm inline-block">{error}</p>
                </div>
              ))}
            <Button className="mt-4" loading={loading} type="submit">
              <Trans>Reset password</Trans>
            </Button>
            <button
              className="text-orange flex items-center mt-5 font-500"
              onClick={() => router.push('/login/')}
            >
              <ArrowLeft size={16} className="mr-1" />
              <Trans>Back to login</Trans>
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ForgotPassword
